import { getRuntime, nonce, currentUser } from './runtime';

const SDK_URL = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
const FALLBACK_APP_ID = '169e1073-0947-4d00-b556-87cc56caf818';
const LEGACY_PRIVATE_LOCATION_TAGS = [
  'pfp_push_latitude',
  'pfp_push_longitude',
  'pfp_push_accuracy_m',
  'pfp_push_location_updated_at',
];
let bootPromise = null;
let lastSubscriptionSyncKey = '';
let lastSubscriptionSyncAt = 0;

function getPushClientContext() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      isIos: false,
      isStandalone: false,
      needsHomeScreenInstall: false,
    };
  }

  const userAgent = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const isTouchMac = platform === 'MacIntel' && Number(navigator.maxTouchPoints || 0) > 1;
  const isIos = /iPad|iPhone|iPod/i.test(userAgent) || isTouchMac;
  const isStandalone = Boolean(
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator?.standalone === true
  );

  return {
    isIos,
    isStandalone,
    needsHomeScreenInstall: Boolean(isIos && !isStandalone),
  };
}

function buildStatus(overrides = {}) {
  const clientContext = getPushClientContext();

  return {
    enabled: true,
    supported: false,
    permission: false,
    permissionState: typeof window !== 'undefined' && typeof window.Notification !== 'undefined'
      ? window.Notification.permission
      : 'default',
    optedIn: false,
    subscriptionId: '',
    ...clientContext,
    ...overrides,
  };
}

function loadOneSignalSdk() {
  if (window.OneSignal || document.querySelector('script[data-pfp-onesignal-sdk="1"]')) {
    return;
  }

  const script = document.createElement('script');
  script.src = SDK_URL;
  script.defer = true;
  script.dataset.pfpOnesignalSdk = '1';
  document.head.appendChild(script);
}

function externalUserId(runtime) {
  const userId = Number(runtime?.user_id || 0);
  return userId > 0 ? `wp_user_${userId}` : '';
}

function userTags(runtime) {
  const tags = {
    source: 'pfandpirat_app',
  };

  if (runtime?.display_name || runtime?.user_name) {
    tags.wp_display_name = String(runtime.display_name || runtime.user_name);
  }

  return tags;
}

function readSupport(OneSignal) {
  const supportValue = OneSignal.Notifications?.isPushSupported;
  return typeof supportValue === 'function'
    ? supportValue.call(OneSignal.Notifications)
    : Boolean(supportValue);
}

function readStatus(OneSignal) {
  const permission = Boolean(OneSignal.Notifications?.permission);
  const permissionState = typeof window.Notification !== 'undefined'
    ? window.Notification.permission
    : (permission ? 'granted' : 'default');

  return buildStatus({
    permission,
    permissionState,
    optedIn: Boolean(OneSignal.User?.PushSubscription?.optedIn),
    subscriptionId: OneSignal.User?.PushSubscription?.id || '',
  });
}

async function syncOneSignalPushSubscriptionStatus(status) {
  const subscriptionId = String(status?.subscriptionId || '').trim();

  if (!subscriptionId || !currentUser?.id || !nonce) {
    return status;
  }

  const syncKey = `${subscriptionId}:${status?.optedIn ? '1' : '0'}`;
  const now = Date.now();
  if (syncKey === lastSubscriptionSyncKey && now - lastSubscriptionSyncAt < 5 * 60 * 1000) {
    return status;
  }

  try {
    const response = await fetch('/wp-json/pfandpirat/v1/push/subscription', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': nonce,
      },
      body: JSON.stringify({
        subscription_id: subscriptionId,
        opted_in: Boolean(status?.optedIn),
      }),
    });

    if (response.ok) {
      lastSubscriptionSyncKey = syncKey;
      lastSubscriptionSyncAt = now;
    }
  } catch (error) {
    if (window.console) {
      console.warn('Pfandpirat Push-Subscription konnte nicht gespeichert werden', error);
    }
  }

  return status;
}

function sanitizeCoordinate(value, min, max) {
  const number = Number(value);
  if (!Number.isFinite(number) || number < min || number > max) {
    return null;
  }

  return Number(number.toFixed(6));
}

function sanitizeAccuracy(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0) {
    return null;
  }

  return Number(number.toFixed(1));
}

async function bootOneSignal(OneSignal, runtime, appId) {
  if (!OneSignal) {
    return null;
  }

  if (!window.__pfandpiratOneSignalAppBooted) {
    window.__pfandpiratOneSignalAppBooted = true;

    try {
      await OneSignal.init({
        appId,
      });
    } catch (error) {
      const message = String(error?.message || error || '');
      if (!/already initialized|already been initialized/i.test(message)) {
        window.__pfandpiratOneSignalAppBooted = false;
        throw error;
      }
    }
  }

  try {
    const id = externalUserId(runtime);
    if (id && typeof OneSignal.login === 'function') {
      await OneSignal.login(id);
    }
  } catch (error) {
    if (window.console) {
      console.warn('Pfandpirat OneSignal Login konnte nicht gesetzt werden', error);
    }
  }

  try {
    if (OneSignal.User?.removeTags) {
      await OneSignal.User.removeTags(LEGACY_PRIVATE_LOCATION_TAGS);
    }

    if (OneSignal.User?.addTags) {
      await OneSignal.User.addTags(userTags(runtime));
    }
  } catch {
    if (window.console) {
      console.warn('Pfandpirat OneSignal Tags konnten nicht datensparsam synchronisiert werden');
    }
  }

  return OneSignal;
}

function withOneSignal(callback) {
  const runtime = getRuntime();
  const appId = getConfiguredOneSignalAppId();

  if (!appId || typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(buildStatus({
      enabled: false,
    }));
  }

  if (window.location?.protocol !== 'https:') {
    return Promise.resolve(buildStatus({
      enabled: false,
    }));
  }

  window.OneSignalDeferred = window.OneSignalDeferred || [];
  loadOneSignalSdk();

  if (!bootPromise) {
    bootPromise = new Promise((resolve) => {
      window.OneSignalDeferred.push(async function initPfandpiratOneSignal(OneSignal) {
        try {
          const initialized = await bootOneSignal(OneSignal, runtime, appId);
          resolve(initialized);
        } catch (error) {
          bootPromise = null;
          if (window.console) {
            console.warn('Pfandpirat OneSignal konnte nicht initialisiert werden', error);
          }
          resolve(null);
        }
      });
    });
  }

  return bootPromise.then(async (OneSignal) => {
    if (!OneSignal) {
      return buildStatus({
        enabled: false,
      });
    }

    return callback(OneSignal);
  });
}

export function getOneSignalAppStatus() {
  return withOneSignal(async (OneSignal) => {
    try {
      const supported = await readSupport(OneSignal);
      const status = { ...readStatus(OneSignal), supported };
      return syncOneSignalPushSubscriptionStatus(status);
    } catch (error) {
      if (window.console) {
        console.warn('Pfandpirat OneSignal Status konnte nicht gelesen werden', error);
      }

      return buildStatus();
    }
  });
}

export function requestOneSignalPushPermission() {
  return withOneSignal(async (OneSignal) => {
    try {
      const supported = await readSupport(OneSignal);

      if (supported && !OneSignal.Notifications?.permission && OneSignal.Notifications?.requestPermission) {
        await OneSignal.Notifications.requestPermission();
      }

      if (supported && OneSignal.User?.PushSubscription?.optIn) {
        await OneSignal.User.PushSubscription.optIn();
      }

      const status = { ...readStatus(OneSignal), supported };
      return syncOneSignalPushSubscriptionStatus(status);
    } catch (error) {
      if (window.console) {
        console.warn('Pfandpirat OneSignal Subscription konnte nicht aktualisiert werden', error);
      }

      let supported = false;
      try {
        supported = await readSupport(OneSignal);
      } catch {}

      return { ...readStatus(OneSignal), supported, error: String(error?.message || error || '') };
    }
  });
}

export function setOneSignalPushSubscriptionEnabled(enabled) {
  if (enabled) {
    return requestOneSignalPushPermission();
  }

  return withOneSignal(async (OneSignal) => {
    try {
      if (OneSignal.User?.PushSubscription?.optOut) {
        await OneSignal.User.PushSubscription.optOut();
      }

      const supported = await readSupport(OneSignal);
      const status = { ...readStatus(OneSignal), supported };
      return syncOneSignalPushSubscriptionStatus(status);
    } catch (error) {
      if (window.console) {
        console.warn('Pfandpirat OneSignal Subscription konnte nicht deaktiviert werden', error);
      }

      let supported = false;
      try {
        supported = await readSupport(OneSignal);
      } catch {}

      return { ...readStatus(OneSignal), supported, error: String(error?.message || error || '') };
    }
  });
}

export function updateOneSignalLocation(position = {}) {
  const latitude = sanitizeCoordinate(position.latitude ?? position.lat, -90, 90);
  const longitude = sanitizeCoordinate(position.longitude ?? position.lng, -180, 180);
  const accuracy = sanitizeAccuracy(position.accuracy);

  if (latitude === null || longitude === null || !currentUser?.id) {
    return Promise.resolve(null);
  }

  return withOneSignal(async (OneSignal) => {
    const status = readStatus(OneSignal);
    const subscriptionId = status.subscriptionId || '';

    if (!subscriptionId || !nonce) {
      return status;
    }

    try {
      await fetch('/wp-json/pfandpirat/v1/push/location', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': nonce,
        },
        body: JSON.stringify({
          subscription_id: subscriptionId,
          latitude,
          longitude,
          accuracy,
        }),
      });
    } catch {
      if (window.console) {
        console.warn('Pfandpirat Push-Standort konnte nicht gespeichert werden');
      }
    }

    return status;
  });
}

export function getConfiguredOneSignalAppId() {
  const runtime = getRuntime();
  return String(runtime?.oneSignalAppId || FALLBACK_APP_ID || '').trim();
}

export function initializeOneSignalForApp() {
  getOneSignalAppStatus();
}
